import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import type { TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { COLORS } from 'muiTheme/colors.ts';
import { SPACING } from '../../../../../../muiTheme/MuiDataGrid';
import TextEditorForTableBlock from '../../../TiptapEditor/components/TextEditorForTableBlock.tsx';
import { TipTapPricingFooterTitle } from '../../../TiptapEditor/components/TipTapPricingFooterTitle.tsx';
import TooltipInputRedux from '../components/TooltipInputRedux';
import { TITLE_KEY } from './PricingTableTotalsFooter.tsx';
import { isJson } from '../../../../../../utils/isJson.ts';

type SharedToggledInputProps = {
  children?: React.ReactNode;
  displayedValue: string;
  clickedCell: string | null;
  inputRefs: React.MutableRefObject<any>;
  inputType?: 'text' | 'number';
  isHidden?: boolean;
  isRowActive?: boolean;
  onCellClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onInputChange: (value: string) => void;
  row: TableRowType;
  value: string | number;
  currencyOrPercentageSymbol?: string;
  isPreview?: boolean;
};

export const ToggledInputWrapper = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'isHidden' && prop !== 'isPreview',
})(({ isHidden = false, isPreview = false }: { isHidden?: boolean; isPreview?: boolean }) => ({
  backgroundColor: isHidden && !isPreview ? COLORS.greyedBackgroundColor : 'transparent',
  lineHeight: isHidden ? 'inherit' : 1,
  paddingRight: SPACING.sm,
  paddingLeft: SPACING.sm,
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  '& > div': {
    width: '100%',
  },
}));

export const TitleTextWrapper = styled('span')({
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
});

const ToggledInput = ({
  currencyOrPercentageSymbol,
  isPreview = false,
  children,
  displayedValue,
  clickedCell,
  inputRefs,
  inputType = 'number',
  isHidden = false,
  isRowActive,
  onCellClick,
  onInputChange,
  row,
  value,
}: SharedToggledInputProps) => {
  const isTipTapEditorActive = inputType === 'text';

  const getDisplayText = (value: string): string => {
    if (!value) return '';
    if (!isJson(value)) return value;

    try {
      const doc = JSON.parse(value);
      const extractText = (node: any): string => {
        if (node.text) return node.text;
        if (node.content) return node.content.map(extractText).join('');
        return '';
      };
      return extractText(doc);
    } catch {
      return value;
    }
  };

  const renderComponent = () => {
    if (isTipTapEditorActive) {
      return (
        <div data-testid="tiptap-wrapper-footer">
          {clickedCell === `${row.id}-${TITLE_KEY}` ? (
            <TipTapPricingFooterTitle id={row.id} hasFocus={clickedCell === `${row.id}-${TITLE_KEY}`} />
          ) : (
            <TextEditorForTableBlock
              editable={false}
              hasFocus={clickedCell === `${row.id}-${TITLE_KEY}`}
              value={isJson(displayedValue) ? JSON.parse(displayedValue) : displayedValue}
            />
          )}
        </div>
      );
    } else {
      return isRowActive ? (
        <TooltipInputRedux
          inputRefs={inputRefs}
          inputType={inputType}
          isHidden={isHidden}
          onInputChange={onInputChange}
          rowId={row.id}
          rowType={row.rowType}
          value={inputType === 'number' ? Number(value) : value}
          currencyOrPercentageSymbol={currencyOrPercentageSymbol}
        >
          {children}
        </TooltipInputRedux>
      ) : (
        <TitleTextWrapper>{row.cellConfig && getDisplayText(displayedValue)}</TitleTextWrapper>
      );
    }
  };

  return (
    <ToggledInputWrapper
      className={isRowActive ? 'py-active-row' : ''}
      data-testid={`${row.rowType?.toLowerCase()}-row-tooltip-wrapper`}
      isHidden={isHidden}
      onClick={onCellClick}
      isPreview={isPreview}
    >
      {renderComponent()}
    </ToggledInputWrapper>
  );
};

export default ToggledInput;
